import type {
  AnswerType,
  ConfigQuestion,
  QuestionGroup,
  QuestionOption,
  SectionType,
} from '@/lib/types'

/**
 * Port of the app's `FactKeyPalette.kt` + `SectionQuestionResolver.kt`: the fact keys a logic box
 * (display condition, material effect) may reference, so the web editor offers exactly the keys the
 * mobile editor offers. Keep the two in sync.
 */

/** A fact key offered as a chip. `groupId` is blank for a section's own question. */
export type PaletteKey = { key: string; type: AnswerType; groupId: string }

/** A question group represented among the palette keys (for the "filter by group" select). */
export type PaletteGroup = { id: string; name: string }

export type FactKeyPalette = { keys: PaletteKey[]; groups: PaletteGroup[] }

/** Palette filter: `null` = all keys, `''` = the section's own keys, otherwise a group id. */
export type PaletteFilter = string | null

/**
 * The questions that apply to a room of `sectionTypeId` (mirrors `SectionQuestionResolver.resolve`):
 * questions of every global group and of the section's selected groups, group by group in group
 * order, then the section's own questions.
 */
export function resolveSectionQuestions(
  sectionType: SectionType | null,
  sectionTypeId: string,
  allQuestions: ConfigQuestion[],
  groups: QuestionGroup[],
): ConfigQuestion[] {
  const selected = new Set(sectionType?.groupIds ?? [])
  const applicable = groups
    .filter((group) => group.isGlobal || selected.has(group.id))
    .sort((left, right) =>
      left.orderIndex !== right.orderIndex
        ? left.orderIndex - right.orderIndex
        : // Kotlin `String.compareTo` compares UTF-16 code units, not locale collation.
          left.id < right.id
          ? -1
          : left.id > right.id
            ? 1
            : 0,
    )
  const byOrder = (left: ConfigQuestion, right: ConfigQuestion) => left.orderIndex - right.orderIndex
  const groupQuestions = applicable.flatMap((group) =>
    allQuestions.filter((question) => question.groupId === group.id).sort(byOrder),
  )
  const sectionQuestions = allQuestions
    .filter((question) => question.groupId === '' && question.sectionTypeId === sectionTypeId)
    .sort(byOrder)
  return [...groupQuestions, ...sectionQuestions]
}

/**
 * The questions a GROUP-scoped logic box may reference (mirrors `groupScopedQuestions`): every group
 * question of the config. A group is reused across rooms, so it cannot depend on one section's own
 * questions, but it may reference any other group.
 */
export function groupScopedQuestions(allQuestions: ConfigQuestion[]): ConfigQuestion[] {
  return allQuestions
    .filter((question) => question.groupId !== '')
    .sort((left, right) => left.orderIndex - right.orderIndex)
}

/**
 * Builds the palette (mirrors `buildFactKeyPalette`): an ordinary question contributes its own fact
 * key; a KEY_SET one BOOLEAN key per item; a REPEATER one key per schema field, typed by the field.
 * Keys are de-duplicated by key (first wins); groups are those represented, in group order.
 */
export function buildFactKeyPalette(
  questions: ConfigQuestion[],
  groups: QuestionGroup[],
  optionsByQuestion: Map<string, QuestionOption[]>,
): FactKeyPalette {
  const keys: PaletteKey[] = []
  const seen = new Set<string>()
  const add = (key: string, type: AnswerType, groupId: string) => {
    if (key.length === 0 || seen.has(key)) return
    seen.add(key)
    keys.push({ key, type, groupId })
  }

  for (const question of questions) {
    const options = optionsByQuestion.get(question.id) ?? []
    if (question.answerType === 'KEY_SET') {
      for (const option of options) add(option.value.trim(), 'BOOLEAN', question.groupId)
    } else if (question.answerType === 'REPEATER' || question.answerType === 'QUESTION_LIST') {
      for (const option of options) add(option.value.trim(), option.fieldType, question.groupId)
    } else if (question.answerType === 'FACT') {
      // FACT stores no answer, so it contributes no key (its factKey is always '').
    } else {
      add(question.factKey.trim(), question.answerType, question.groupId)
    }
  }

  const represented = new Set(keys.map((item) => item.groupId).filter((id) => id !== ''))
  const paletteGroups = groups
    .filter((group) => represented.has(group.id))
    .sort((left, right) => left.orderIndex - right.orderIndex)
    .map((group) => ({ id: group.id, name: group.name }))

  return { keys, groups: paletteGroups }
}

/** Applies the group filter (mirrors `keysForGroup`). */
export function keysForFilter(keys: PaletteKey[], filter: PaletteFilter): PaletteKey[] {
  if (filter === null) return keys
  if (filter === '') return keys.filter((item) => item.groupId === '')
  return keys.filter((item) => item.groupId === filter)
}


/**
 * Inserts `token` at the cursor of `element` (replacing any selection), hands the new text to `apply`
 * and puts the caret right after the token once React has re-rendered. Without an element (the field
 * was never focused) the token is appended.
 */
export function insertAtCursor(
  element: HTMLInputElement | HTMLTextAreaElement | null,
  current: string,
  token: string,
  apply: (next: string) => void,
) {
  const start = element?.selectionStart ?? current.length
  const end = element?.selectionEnd ?? current.length
  apply(current.slice(0, start) + token + current.slice(end))
  requestAnimationFrame(() => {
    if (!element) return
    element.focus()
    const caret = start + token.length
    element.setSelectionRange(caret, caret)
  })
}
